// CORA Theme Definition
// Tema completo autocontenido para el cliente CORA

import { Theme, Colors } from '../types';

// ========================================
// COLORES CORA (DEFINICIÓN COMPLETA)
// ========================================

const coraLightColors: Colors = {
  // Colores principales
  white: '#FFFFFF',
  black: '#111111',

  // Grises (todos los valores reales del proyecto)
  greyVeryLight: '#F4F1F2',
  greyVerySoft: '#F6F6F6',
  greyLight: '#D9D9D9',
  greySilver: '#959595',
  lightGreyWhite: '#CCCCCC',
  greyBackground: '#19191D66',
  greyBoard: '#4A4B57',
  greyVeryDark: '#575757',
  greyDark: '#757575',
  greyClear: '#F5F5F5',
  greySoftLight: '#A0A0A0',
  paleGrey: '#EAEAEA',
  paleDarkGrey: '#AEAEAE',
  darkGrey: '#3D3D3D',
  mediumGrey: '#9F9F9F',
  darkGrayishGrey: '#747474',
  pureGrey: '#808080',

  // Rojos (todos los valores reales del proyecto)
  softRed: '#FEE8E7',
  softDarkWhite: '#F0EAEC',
  red: '#C8372D',
  lightRed: '#FFF7F6',
  verySoftRed: '#FAF1F0',
  veryClearRed: '#F8EBEB',
  clearRed: '#FFD9D7',
  lightRose: '#FBE2E0',
  lightCoralRed: '#F6655A',
  darkBrickRed: '#A62F27',
  deepBrickRed: '#691E19',
  garnet: '#721C34',
  garnetLight: '#721C3433',
  garnetClear: '#721C341A',
  darkBurgundyRed: '#521324',
  veryDarkBurgundyRed: '#541325',
  deepRedWine: '#440F1D',
  DustyRose: '#D88C87',
  lightDustyRose: '#AE7F8C',
  darkRoseRed: '#8E495C',
  paleRose: '#F1EAEC',
  lightPink: '#DC8E88',
  lightPastelPink: '#F1D8D6',
  veryClearPink: '#F1DDE3',

  // Verdes (todos los valores reales del proyecto)
  softGreen: '#ECF7ED',
  green: '#78B85A',
  veryClearGreen: '#EEF5EF',
  mossGreen: '#5B8B44',
  forestGreen: '#37833B',
  darkForestGreen: '#426531',
  lightPearGreen: '#B1D3A0',
  veryLightPastelGreen: '#EAFDED',
  sageGreen: '#8DB38E',

  // Otros colores (todos los valores reales del proyecto)
  gold: '#FFA02F',
  goldSoft: '#F8C28A',
  // Ámbar de `gold` para LETRA sobre fondo claro: `gold` es un color de fondo
  // (1,36:1 como texto). Este da ≥4,5:1 sobre blanco y sobre las filas grises.
  goldText: '#9A5500', // 5,7:1 sobre blanco
  // Etiquetas: letra tonal (≥4,5:1 sobre su fondo) y fondos con letra blanca legible.
  tagTextGold: '#402300',
  tagTextBlue: '#091A2A',
  tagTextBlueSoft: '#113045',
  tagTextGreen: '#102108',
  tagTextOrange: '#402300',
  tagTextPink: '#80009E',
  tagSurfaceRed: '#C8372D',
  tagSurfaceTeal: '#008276',
  pink: '#F9DFFF',
  purple: '#A92DC8',
  blue: '#3387D4',
  blueSoft: '#9AC0DA',
  blueRouteInProgress: '#2E7BA6', // RV en progreso (contraste 4,66:1 con blanco)
  teal: '#009688', // Pendent de certificar (post-validación, pendiente cobro Generalitat)

  // Colores específicos ROIS (valores por defecto para CORA)
  blueOT: '#3387D4', // Usa el azul estándar de CORA
  blueOTSecondary: '#3387D4',
  blueActuation: '#3387D4',
  blueActuationSecondary: '#3387D4',
  blueCommunication: '#9AC0DA',
  blueCommunicationSecondary: '#9AC0DA',
  backgroundLila: '#F6F6F6', // Usa gris muy suave de CORA
  navigationActive: '#FAF1F0', // Usa verySoftRed de CORA para compatibilidad

  // Colores adicionales para componentes
  greenTransparent: 'rgba(120, 184, 90, 0.1)', // Verde CORA con 10% opacidad
  orangeTransparent: 'rgba(255, 160, 47, 0.1)', // Gold CORA con 10% opacidad
  secondaryOrange: '#FFA02F', // Gold CORA como naranja secundario
  background: '#FFFFFF', // Fondo principal
  text: '#111111', // Texto principal
  border: '#D9D9D9', // Borde (greyLight)
  primary: '#721C34', // Color primario CORA (garnet)
  error: '#C8372D', // Color error CORA (red)

  // Estados de segmentos de rutas de vigilancia (Timer iteration)
  routeStatus: {
    completed: '#78B85A', // = green CORA
    paused: '#FFA02F', // = secondaryOrange CORA (gold)
    active: '#3387D4', // = blue CORA
  },
};

const coraDarkColors: Colors = {
  // Colores principales (invertidos para tema oscuro)
  white: '#1a1a1a',
  black: '#ffffff',

  // Grises (invertidos para tema oscuro)
  greyVeryLight: '#2a2a2a',
  greyVerySoft: '#333333',
  greyLight: '#404040',
  greySilver: '#6a6a6a',
  lightGreyWhite: '#333333',
  greyBackground: '#19191D66',
  greyBoard: '#5a5a5a',
  greyVeryDark: '#f0f0f0',
  greyDark: '#8a8a8a',
  greyClear: '#2d2d2d',
  greySoftLight: '#5f5f5f',
  paleGrey: '#151515',
  paleDarkGrey: '#515151',
  darkGrey: '#c2c2c2',
  mediumGrey: '#606060',
  darkGrayishGrey: '#8b8b8b',
  pureGrey: '#7f7f7f',

  // Rojos (mantener colores de marca para tema oscuro)
  softRed: '#FEE8E7',
  softDarkWhite: '#F0EAEC',
  red: '#C8372D',
  lightRed: '#FFF7F6',
  verySoftRed: '#FAF1F0',
  veryClearRed: '#F8EBEB',
  clearRed: '#FFD9D7',
  lightRose: '#FBE2E0',
  lightCoralRed: '#F6655A',
  darkBrickRed: '#A62F27',
  deepBrickRed: '#691E19',
  garnet: '#721C34',
  garnetLight: '#721C3433',
  garnetClear: '#721C341A',
  darkBurgundyRed: '#521324',
  veryDarkBurgundyRed: '#541325',
  deepRedWine: '#440F1D',
  DustyRose: '#D88C87',
  lightDustyRose: '#AE7F8C',
  darkRoseRed: '#8E495C',
  paleRose: '#F1EAEC',
  lightPink: '#DC8E88',
  lightPastelPink: '#F1D8D6',
  veryClearPink: '#F1DDE3',

  // Verdes (mantener para tema oscuro)
  softGreen: '#ECF7ED',
  green: '#78B85A',
  veryClearGreen: '#EEF5EF',
  mossGreen: '#5B8B44',
  forestGreen: '#37833B',
  darkForestGreen: '#426531',
  lightPearGreen: '#B1D3A0',
  veryLightPastelGreen: '#EAFDED',
  sageGreen: '#8DB38E',

  // Otros colores (mantener para tema oscuro)
  gold: '#FFA02F',
  goldSoft: '#F8C28A',
  goldText: '#FFA02F', // oscuro: el propio gold, 8,6:1 sobre #1a1a1a
  // Etiquetas: letra tonal (≥4,5:1 sobre su fondo) y fondos con letra blanca legible. En oscuro la letra «white» es #1a1a1a.
  tagTextGold: '#402300',
  tagTextBlue: '#091A2A',
  tagTextBlueSoft: '#113045',
  tagTextGreen: '#102108',
  tagTextOrange: '#402300',
  tagTextPink: '#80009E',
  tagSurfaceRed: '#D95D54',
  tagSurfaceTeal: '#009688',
  pink: '#F9DFFF',
  purple: '#A92DC8',
  blue: '#3387D4',
  blueSoft: '#9AC0DA',
  blueRouteInProgress: '#2E7BA6', // RV en progreso (contraste 4,66:1 con blanco)
  teal: '#009688', // Pendent de certificar (post-validación, pendiente cobro Generalitat)

  // Colores específicos ROIS (valores por defecto para CORA - tema oscuro)
  blueOT: '#3387D4', // Usa el azul estándar de CORA
  blueOTSecondary: '#3387D4',
  blueActuation: '#3387D4',
  blueActuationSecondary: '#3387D4',
  blueCommunication: '#9AC0DA',
  blueCommunicationSecondary: '#9AC0DA',
  backgroundLila: '#2a2a2a', // Gris oscuro para tema oscuro
  navigationActive: '#FAF1F0', // Usa verySoftRed de CORA para compatibilidad

  // Colores adicionales para componentes (tema oscuro)
  greenTransparent: 'rgba(120, 184, 90, 0.1)', // Verde CORA con 10% opacidad
  orangeTransparent: 'rgba(255, 160, 47, 0.1)', // Gold CORA con 10% opacidad
  secondaryOrange: '#FFA02F', // Gold CORA como naranja secundario
  background: '#1a1a1a', // Fondo principal oscuro
  text: '#ffffff', // Texto principal claro
  border: '#404040', // Borde oscuro (greyLight dark)
  primary: '#721C34', // Color primario CORA (mantenido)
  error: '#C8372D', // Color error CORA (mantenido)

  // Estados de segmentos de rutas de vigilancia (Timer iteration)
  routeStatus: {
    completed: '#78B85A',
    paused: '#FFA02F',
    active: '#3387D4',
  },
};

// ========================================
// TEMA COMPLETO CORA
// ========================================

const coraBaseTheme = {
  // Espaciado CORA (valores reales del proyecto)
  spaces: {
    xxxs: 0.5,
    xxs: 0.8,
    xs: 1.4,
    s: 1.6,
    m: 2.5,
    l: 3.2,
    xl: 4,
    xxl: 6.4,
    xxxl: 8,
  },

  // Breakpoints CORA (valores reales del proyecto)
  breakPoints: {
    portraitTablet: 76.7,
    landscapeTablet: 102.3,
    smallDesktop: 186,
  },

  percentBaseResponsive: 0.625,

  // Tipografía CORA (valores reales del proyecto)
  fontSize: {
    XXXS: '1rem',
    XXS: '1.2rem',
    XS: '1.4rem',
    S: '1.6rem',
    M: '1.8rem',
    L: '2rem',
    XL: '3.4rem',
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semiBold: 600,
  },

  // Bordes CORA
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  // Sombras CORA
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    header: '0 0.2rem 0.4rem rgba(0, 0, 0, 0.24)',
    modal: '0.03rem 0.5rem 1.3rem rgba(0, 0, 0, 0.15)',
    toast: '0 0.8rem 0.8rem rgba(0, 0, 0, 0.25)',
    dropdown: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
    card: '0 2px 4px rgba(0, 0, 0, 0.1)',
    camera: '0 0 13px 3px rgba(0, 0, 0, 0.5)',
    intense: '-5px 8px 19px -7px rgba(0, 0, 0, 0.74)',
  },

  // Transiciones CORA
  transitions: {
    fast: '150ms ease-in-out',
    normal: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },

  // Z-Index CORA
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },

  // Tamaños CORA
  sizes: {
    logoWidth: '9rem',
    logoHeight: '2.6rem',
    headerMaxHeight: '7.7rem',
    iconSize: '2rem',
    buttonHeight: '4rem',
    modalMinWidth: '33.8rem',
  },

  // Opacidad CORA
  opacity: {
    disabled: 0.6,
    overlay: 0.24,
    modalOverlay: 0.3,
    hover: 0.8,
    focus: 0.15,
  },

  // Helpers calculados CORA (valores reales del proyecto)
  spacing: {
    xxxs: '0.5rem',
    xxs: '0.8rem',
    xs: '1.4rem',
    s: '1.6rem',
    m: '2.5rem',
    l: '3.2rem',
    xl: '4rem',
    xxl: '6.4rem',
    xxxl: '8rem',
  },
};

// ========================================
// TEMAS CORA EXPORTADOS
// ========================================

export const coraLightTheme: Theme = {
  ...coraBaseTheme,
  colors: coraLightColors,
};

export const coraDarkTheme: Theme = {
  ...coraBaseTheme,
  colors: coraDarkColors,
};

// Export de todos los temas CORA
export const coraThemes = {
  light: coraLightTheme,
  dark: coraDarkTheme,
} as const;
